import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import get from 'lodash/get';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Button } from 'src/shared/components/Button';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { CareOption } from 'src/generated/webapp_gql';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import running from 'src/shared/svg/running.svg';
import sessionTopics from 'src/shared/svg/session-topics.svg';
import soulAndMindful from 'src/shared/svg/soul-and-mindful.svg';
import trophy from 'src/shared/svg/trophy.svg';
import { useAcceptCareRecommendationMutation } from '../documents/acceptCareRecommendationMutation.generated';

const TileWrapper = styled.div({
    padding: 20,
    marginLeft: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '32.5%',
    border: `1px solid ${color.linkWaterLight}`,
    alignItems: 'center',
    borderRadius: 12,
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
});

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
    },
    font.heavy(font.size.medium),
);

interface TileDescriptionProps {
    quote?: boolean;
}

const TileDescription = styled.p<TileDescriptionProps>(
    props => ({
        textAlign: 'center',
        color: props.quote ? color.blueBayoux : color.steelGray,
        fontStyle: props.quote ? 'oblique' : 'normal',
    }),
    font.book(font.size.base),
);

interface TileProps {
    description: string;
    title?: string;
    img: string;
}

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    width: '1.5rem',
    height: '1.5rem',
    backgroundSize: 'cover',
    backgroundColor: color.lightCyan,
    backgroundPositionX: 'center',
}));

const Tile: React.FC<TileProps> = ({ description, title, img }) => (
    <TileWrapper>
        <div
            style={{
                padding: 15,
                backgroundColor: color.lightCyan,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 10,
            }}
        >
            <StyledIcon iconUrl={img} />
        </div>
        {title && <TileTitle>{title}</TileTitle>}
        <TileDescription>{description}</TileDescription>
    </TileWrapper>
);

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    maxHeight: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    [mediaQueries.sm]: {
        marginTop: 50,
    },
});

const Title = styled.h3(
    {
        color: color.pickledBluewood,
        marginTop: 32,
        marginBottom: 20,
    },
    font.black(font.size.headerMedium),
);

const Description = styled.p({
    color: color.steelGray,
    maxWidth: 820,
    marginBottom: 30,
    textAlign: 'center',
    ...font.book(font.size.mediumExtended),
});

const SubTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
    },
    font.heavy(font.size.mediumExtended),
);

const TilesWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 1050,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
});

const ButtonsWrapper = styled.div({
    marginTop: 40,
    display: 'flex',
    flexDirection: 'row',
});

export enum OnMyOwnPageTestID {
    NotNow = 'OnMyOwnPageTestID.NotNow',
    GetProgramRecommendations = 'OnMyOwnPageTestID.GetProgramRecommendations',
}

export const OnMyOwnPage: React.FC<RouteComponentProps> = ({ history }) => {
    const { resetDate } = useSelector(getSessionUtilizationInfo);
    const [acceptCareRecommendation, {}] = useAcceptCareRecommendationMutation({
        client: appClient,
    });
    const { careRecommendations } = useSelector(getUserInformation);
    const { showYoureOnYourWayWeb } = useFlags();

    const { t } = useTranslation();

    const data = [
        {
            title: t('onboarding:on_my_own_page.tiles.tile_1.title'),
            description: t('onboarding:on_my_own_page.tiles.tile_1.description'),
            img: trophy,
        },
        {
            title: t('onboarding:on_my_own_page.tiles.tile_2.title'),
            description: t('onboarding:on_my_own_page.tiles.tile_2.description'),
            img: running,
        },
        {
            title: t('onboarding:on_my_own_page.tiles.tile_3.title'),
            description: t('onboarding:on_my_own_page.tiles.tile_3.description'),
            img: sessionTopics,
        },
    ];

    const handleContinue = async () => {
        try {
            const res = await acceptCareRecommendation({
                variables: {
                    uuid: careRecommendations[0].uuid,
                    outcome: CareOption.DigitalPrograms,
                },
            });
            const careUuid = get(res, 'data.acceptCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Failed to accept care recommendation');
            }

            showYoureOnYourWayWeb
                ? history.push('/youre-on-your-way')
                : history.push('/get-program-recommendations');
        } catch (error) {
            showYoureOnYourWayWeb
                ? history.push('/youre-on-your-way')
                : history.push('/get-program-recommendations');

            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to accept a care recommendation');

                Sentry.captureException(error);
            });
        }
    };

    return (
        <Wrapper>
            <img src={soulAndMindful} />
            <Title>{t('onboarding:on_my_own_page.title')}</Title>
            <Description>
                {t('onboarding:on_my_own_page.description')}{' '}
                <span
                    style={{
                        ...font.heavy(font.size.mediumExtended),
                    }}
                >
                    {t('onboarding:on_my_own_page.unlimited_digital_programs')}
                </span>
                {t('onboarding:on_my_own_page.till_date', {
                    date: resetDate.format('MMM Do, YYYY'),
                })}
            </Description>
            <SubTitle>{t('onboarding:on_my_own_page.subtitle')}</SubTitle>
            <TilesWrapper>
                {data.map(tileData => (
                    <Tile
                        description={tileData.description}
                        title={tileData.title}
                        key={tileData.description}
                        img={tileData.img}
                    />
                ))}
            </TilesWrapper>
            <ButtonsWrapper>
                <Button
                    onClick={() => {
                        showYoureOnYourWayWeb
                            ? history.push('/youre-on-your-way')
                            : history.push('/');
                    }}
                    marginRight={10}
                    type="white"
                    withShadow
                    testID={OnMyOwnPageTestID.NotNow}
                >
                    {t('onboarding:on_my_own_page.not_now')}
                </Button>
                <Button
                    onClick={handleContinue}
                    horizontalPadding="medium"
                    withShadow
                    testID={OnMyOwnPageTestID.GetProgramRecommendations}
                >
                    {showYoureOnYourWayWeb
                        ? t('onboarding:on_my_own_page.continue')
                        : t('onboarding:on_my_own_page.get_program_recommendations')}
                </Button>
            </ButtonsWrapper>
        </Wrapper>
    );
};
